import { useState } from 'react';
import { EmailData, SplitDocumentsControls } from './email-viewer-types';
import { SplitDocumentsSection } from './email-attachments';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowsOutIcon, ArrowSquareOutIcon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PDFViewer } from '@/components/common/pdf-viewer';
import { ExcelDocViewer } from '@/components/xlsx-viewer/ExcelDocViewer';
import { isExcelFile } from './utils';
import { ImageWithSmartScaling } from '@/components/common/ImageWithSmartScaling';
import { T, useGT } from 'gt-react';
import { FullscreenOverlay } from '@/components/common/FullscreenOverlay';

/**
 * Height of a single attachment preview when the caller doesn't pick one.
 * Callers that hand the viewer a dialog-sized box (the 849 email dialog) pass
 * a viewport-relative height instead, so a wide PDF or a 30-column workbook
 * isn't squeezed into a 450px porthole.
 */
const DEFAULT_VIEWER_HEIGHT = 450;

interface ViewerAttachment {
  id: string;
  documentId?: string;
  fileName: string;
  sizeBytes: number;
  mimeType?: string;
  s3Url: string;
}

function getAllAttachments(emailData: EmailData): ViewerAttachment[] {
  return [
    ...(emailData.attachments ?? []),
    ...(emailData.documentAssociations ?? []).map((association) => ({
      id: association.id,
      documentId: association.document.id,
      fileName:
        association.document.originalFileName || association.document.name,
      sizeBytes: association.document.fileSizeBytes ?? 0,
      mimeType: association.document.mimeType,
      s3Url: association.document.url,
    })),
  ];
}

/**
 * A PDF preview with a fullscreen escape hatch. Fit-to-width inside an
 * attachment card is unreadable for a landscape or wide-format PDF, and the
 * card has no room to scroll horizontally — so let the document take the whole
 * viewport on demand, the same affordance `XlsxViewer` already has.
 *
 * The toggle is rendered by the card header rather than overlaid on the
 * viewer: `PDFViewer`'s own toolbar already owns both top corners.
 */
function PdfAttachmentPreview({
  url,
  fileName,
  height,
  isFullscreen,
  onExitFullscreen,
  onRefreshData,
}: {
  url: string;
  fileName: string;
  height: string | number;
  isFullscreen: boolean;
  onExitFullscreen: () => void;
  onRefreshData?: () => void;
}) {
  if (isFullscreen) {
    return (
      <FullscreenOverlay title={fileName} onClose={onExitFullscreen}>
        <div className="flex-1 min-h-0">
          <PDFViewer url={url} pageMode="single" onUrlExpired={onRefreshData} />
        </div>
      </FullscreenOverlay>
    );
  }

  return (
    <div
      className="flex flex-col border rounded-md overflow-hidden bg-background"
      style={{ height }}
    >
      <PDFViewer url={url} pageMode="single" onUrlExpired={onRefreshData} />
    </div>
  );
}

function isPdfAttachment(attachment: ViewerAttachment): boolean {
  return (
    attachment.mimeType === 'application/pdf' ||
    attachment.fileName?.toLowerCase().endsWith('.pdf')
  );
}

export function EmailAttachment({
  emailData,
  onRefreshData,
  splitDocuments,
  viewerHeight = DEFAULT_VIEWER_HEIGHT,
}: {
  emailData: EmailData;
  onRefreshData?: () => void;
  splitDocuments?: SplitDocumentsControls;
  /** Height of each document preview. See `DEFAULT_VIEWER_HEIGHT`. */
  viewerHeight?: string | number;
}) {
  const allAttachments = getAllAttachments(emailData).sort((a, b) => {
    const aIsPdf = isPdfAttachment(a);
    const bIsPdf = isPdfAttachment(b);
    if (aIsPdf && !bIsPdf) return -1;
    if (!aIsPdf && bIsPdf) return 1;
    return 0;
  });

  const splitDocumentsSection = splitDocuments ? (
    <SplitDocumentsSection
      count={emailData.splitAttachmentsCount ?? 0}
      attachments={emailData.splitAttachments?.map((attachment) => ({
        id: attachment.id,
        documentId: attachment.id,
        fileName: attachment.fileName,
        sizeBytes: attachment.sizeBytes,
        mimeType: attachment.mimeType,
        s3Url: attachment.s3Url,
      }))}
      show={splitDocuments.show}
      onToggle={splitDocuments.onToggle}
      isLoading={splitDocuments.isLoading}
      onRefreshData={onRefreshData}
    />
  ) : null;

  if (!allAttachments.length) {
    return (
      <Card>
        <CardContent className="flex flex-col justify-center items-center p-8">
          <span className="text-xs text-gray-400 italic tracking-wide opacity-20">
            <T context="email viewer">No attachments found</T>
          </span>
          {splitDocumentsSection}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="p-1 space-y-2">
      {allAttachments.map((attachment) => (
        <AttachmentCard
          key={attachment.id}
          attachment={attachment}
          viewerHeight={viewerHeight}
          onRefreshData={onRefreshData}
        />
      ))}
      {splitDocumentsSection}
    </div>
  );
}

function AttachmentCard({
  attachment,
  viewerHeight,
  onRefreshData,
}: {
  attachment: ViewerAttachment;
  viewerHeight: string | number;
  onRefreshData?: () => void;
}) {
  const gt = useGT();
  const [isPdfFullscreen, setIsPdfFullscreen] = useState(false);
  const isPdf = isPdfAttachment(attachment);

  return (
    <Card onClick={(e) => e.stopPropagation()}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          {/* eslint-disable i18next/no-literal-string -- file size display with KB unit abbreviation */}
          <div className="text-sm font-medium break-words">
            {attachment.fileName} ({(attachment.sizeBytes / 1024).toFixed(2)}{' '}
            KB)
          </div>
          {/* eslint-enable i18next/no-literal-string */}
          <div className="flex items-center shrink-0">
            {isPdf && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsPdfFullscreen(true);
                    }}
                    className="h-auto p-2"
                    aria-label={gt('View attachment fullscreen', {
                      context: 'accessible name for the pdf fullscreen button',
                    })}
                  >
                    <ArrowsOutIcon className="w-4 h-4" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <T context="pdf attachment fullscreen tooltip">Fullscreen</T>
                </TooltipContent>
              </Tooltip>
            )}
            <Button
              variant="link"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                window.open(attachment.s3Url, '_blank');
              }}
              className="h-auto p-2"
            >
              <ArrowSquareOutIcon className="w-4 h-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Content based on file type */}
        {attachment.mimeType?.startsWith('image/') ? (
          <ImageWithSmartScaling
            src={attachment.s3Url}
            alt={attachment.fileName}
            fileName={attachment.fileName}
            sizeBytes={attachment.sizeBytes}
          />
        ) : isPdf ? (
          <PdfAttachmentPreview
            url={attachment.s3Url}
            fileName={attachment.fileName}
            height={viewerHeight}
            isFullscreen={isPdfFullscreen}
            onExitFullscreen={() => setIsPdfFullscreen(false)}
            onRefreshData={onRefreshData}
          />
        ) : isExcelFile(attachment) ? (
          <ExcelDocViewer
            url={attachment.s3Url}
            fileName={attachment.fileName}
            documentId={attachment.documentId}
            height={viewerHeight}
          />
        ) : (
          <a
            href={attachment.s3Url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            <T context="action, download a file">Download File</T>
          </a>
        )}
      </CardContent>
    </Card>
  );
}
